// src/components/CollectionBreakdown.jsx

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, Doughnut } from "react-chartjs-2";
import { supabase } from "../lib/supabaseClient";
import { getArtTypeCanonicalKey } from "../utils/artTypeTaxonomy";
import { getTypeOfArtLabel } from "../utils/artkivLabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const ALL = "ALL";
const UNKNOWN_KEY = "unknown";
const UNKNOWN_COLOR = "#e5e7eb";

const makePalette = (n, { s = 42, l = 62 } = {}) =>
  Array.from({ length: n }, (_, i) =>
    `hsl(${Math.round((360 / Math.max(n, 1)) * i)}, ${s}%, ${l}%)`
  );

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const parseYear = (str) => {
  const match = String(str || "").match(/\d{4}/);
  return match ? parseInt(match[0], 10) : null;
};

const toDecadeKey = (year) => {
  const y = parseYear(year);
  if (!y) return UNKNOWN_KEY;
  return `${Math.floor(y / 10) * 10}s`;
};

const FALLBACK_MEDIUM_LABELS_FR = {
  unknown: "Non renseigné",
  painting: "Peinture",
  photography: "Photographie",
  sculpture: "Sculpture",
  drawing: "Dessin",
  work_on_paper: "Œuvres sur papier",
  works_on_paper: "Œuvres sur papier",
  prints: "Gravure / Estampe",
  mixed_media: "Techniques mixtes",
  installation: "Installation",
  design: "Design",
  ceramics: "Céramique",
  textile: "Textile / Tapisserie",
  film_video: "Film / Vidéo",
  video: "Vidéo",
  nft: "NFT / Art numérique",
  digital_art: "Art numérique",
  other: "Autre",

  oil_on_canvas: "Peinture",
  acrylic_on_canvas: "Peinture",
  acrylic_and_spray_paint_on_canvas: "Peinture",
  vinyl_paint: "Peinture",
  stencil_and_acrylic_on_canvas: "Peinture",
};

const translateWithFallback = (t, keys, fallback) => {
  for (const key of keys) {
    if (!key) continue;
    const translated = t(key, { defaultValue: "" });
    if (translated && translated !== key) return translated;
  }
  return fallback;
};

const getMediumCanonicalKey = (artwork) => {
  const candidates = [
    artwork?.medium_category,
    artwork?.medium_family_key,
    artwork?.medium_normalized_key,
    artwork?.medium,
  ];

  for (const candidate of candidates) {
    const key = normalizeKey(candidate);
    if (!key) continue;

    if (FALLBACK_MEDIUM_LABELS_FR[key] && !key.includes("_on_")) return key;

    if (
      [
        "peinture",
        "painting",
        "oil_on_canvas",
        "acrylic_on_canvas",
        "acrylic_and_spray_paint_on_canvas",
        "stencil_and_acrylic_on_canvas",
        "vinyl_paint",
        "huile",
        "acrylique",
      ].includes(key)
    ) {
      return "painting";
    }

    if (key.includes("photo") || key.includes("tirage") || key.includes("polaroid")) {
      return "photography";
    }

    if (
      key.includes("paint") ||
      key.includes("peinture") ||
      key.includes("huile") ||
      key.includes("acrylique") ||
      key.includes("acrylic") ||
      key.includes("oil")
    ) {
      return "painting";
    }

    if (key.includes("sculpt")) return "sculpture";

    if (
      key.includes("serigraph") ||
      key.includes("litho") ||
      key.includes("gravure") ||
      key.includes("estampe") ||
      key.includes("print")
    ) {
      return "prints";
    }

    if (
      key.includes("crayon") ||
      key.includes("mine") ||
      key.includes("charcoal") ||
      key.includes("fusain") ||
      key.includes("pencil")
    ) {
      return "drawing";
    }

    if (
      key.includes("paper") ||
      key.includes("papier") ||
      key.includes("gouache") ||
      key.includes("encre") ||
      key.includes("ink") ||
      key.includes("lavis")
    ) {
      return "work_on_paper";
    }

    if (key.includes("mixed") || key.includes("mixte") || key.includes("collage")) {
      return "mixed_media";
    }

    if (key.includes("install")) return "installation";
    if (key.includes("digital") || key.includes("numerique") || key.includes("nft")) return "digital_art";

    return key;
  }

  return UNKNOWN_KEY;
};

// Taxonomie « type d'art » centralisée (départements des maisons de ventes +
// équivalents FR/EN, mouvements → période, médiums mal saisis → « autre »).
// Voir src/utils/artTypeTaxonomy.js.
const getTypeCanonicalKey = (value) => getArtTypeCanonicalKey(value);

const translateMedium = (t, key) => {
  const canonical = key || UNKNOWN_KEY;
  return translateWithFallback(
    t,
    [
      `artworks.mediums.${canonical}`,
      `artwork.mediums.${canonical}`,
      `mediums.${canonical}`,
      `artworks.medium.${canonical}`,
      `artwork.medium.${canonical}`,
    ],
    FALLBACK_MEDIUM_LABELS_FR[canonical] || canonical
  );
};

const translatePeriod = (t, key) => {
  if (!key || key === UNKNOWN_KEY) {
    return translateWithFallback(
      t,
      ["common.unknown", "artworks.period.unknown", "artwork.period.unknown"],
      "Inconnue"
    );
  }

  // Décennie « 1940s » → « 1940–49 » (le « s » collé se lit mal sur l'axe).
  const decade = String(key).match(/^(\d{4})s$/);
  if (decade) {
    const start = Number(decade[1]);
    return `${start}–${String((start + 9) % 100).padStart(2, "0")}`;
  }

  return key;
};

const isUnknownKey = (key) => !key || key === UNKNOWN_KEY;

const greyUnknowns = (keys, colors) =>
  keys.map((key, i) => (isUnknownKey(key) ? UNKNOWN_COLOR : colors[i]));

export default function CollectionBreakdown({ userId }) {
  const { t, i18n } = useTranslation();

  const [collections, setCollections] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(ALL);
  const [locationBreakdownMode, setLocationBreakdownMode] = useState("location");
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [locationData, setLocationData] = useState({ keys: [], labels: [], values: [] });
  const [mediumData, setMediumData] = useState({ keys: [], labels: [], values: [] });
  const [typeData, setTypeData] = useState({ keys: [], labels: [], values: [] });
  const [periodData, setPeriodData] = useState({ keys: [], labels: [], values: [] });
  const [periodUnknown, setPeriodUnknown] = useState(0);
  const [periodUnknownLot, setPeriodUnknownLot] = useState(0);
  const [valueByMedium, setValueByMedium] = useState({ keys: [], labels: [], values: [] });
  const [artistData, setArtistData] = useState({ keys: [], labels: [], values: [] });

  useEffect(() => {
    if (!userId) return;

    supabase
      .from("collections")
      .select("id, title")
      .eq("client_id", userId)
      .order("title")
      .then(({ data, error }) => {
        if (error) console.error("CollectionBreakdown: collections", error);
        setCollections(data || []);
      });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    supabase
      .from("locations")
      .select("id, name, type")
      .eq("client_id", userId)
      .order("name")
      .then(({ data, error }) => {
        if (error) console.error("CollectionBreakdown: locations", error);
        setLocations(data || []);
      });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    setLoadError(null);

    const load = async () => {
      try {
        let query = supabase
          .from("artworks")
          .select(
            "id, artist, medium, medium_category, medium_family_key, medium_normalized_key, typeOfArt, year, value, estimated_value, collection_id, location_id, lot_id"
          )
          .eq("client_id", userId);

        if (selectedCollectionId !== ALL) {
          query = query.eq("collection_id", selectedCollectionId);
        }

        const { data, error } = await query;
        if (error) throw error;

        setArtworks(data || []);
      } catch (e) {
        console.error("CollectionBreakdown: artworks", e);
        setLoadError(e?.message || t("round36.collectionBreakdown.loadError"));
        setArtworks([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId, selectedCollectionId, t]);

  useEffect(() => {
    if (!artworks.length) {
      setLocationData({ keys: [], labels: [], values: [] });
      setMediumData({ keys: [], labels: [], values: [] });
      setTypeData({ keys: [], labels: [], values: [] });
      setPeriodData({ keys: [], labels: [], values: [] });
      setPeriodUnknown(0);
      setPeriodUnknownLot(0);
      setValueByMedium({ keys: [], labels: [], values: [] });
      setArtistData({ keys: [], labels: [], values: [] });
      return;
    }

    const locNameById = Object.fromEntries((locations || []).map((l) => [l.id, l.name]));
    const colTitleById = Object.fromEntries((collections || []).map((c) => [c.id, c.title]));

    const mediumMap = {};
    const typeMap = {};
    const decadeMap = {};
    const locationMap = {};
    const valueMediumMap = {};
    const artistMap = {};
    let unknownInLot = 0; // œuvres sans année qui appartiennent à un lot

    for (const artwork of artworks) {
      const mediumKey = getMediumCanonicalKey(artwork);
      mediumMap[mediumKey] = (mediumMap[mediumKey] || 0) + 1;

      const value = Number(artwork?.value ?? artwork?.estimated_value ?? 0) || 0;
      valueMediumMap[mediumKey] = (valueMediumMap[mediumKey] || 0) + value;

      const typeKey = getTypeCanonicalKey(artwork?.typeOfArt);
      typeMap[typeKey] = (typeMap[typeKey] || 0) + 1;

      const decadeKey = toDecadeKey(artwork?.year);
      decadeMap[decadeKey] = (decadeMap[decadeKey] || 0) + 1;
      if (decadeKey === UNKNOWN_KEY && artwork?.lot_id) unknownInLot += 1;

      const locLabel =
        locationBreakdownMode === "collection"
          ? artwork?.collection_id && colTitleById[artwork.collection_id]
            ? colTitleById[artwork.collection_id]
            : t("round36.collectionBreakdown.unclassified")
          : artwork?.location_id && locNameById[artwork.location_id]
          ? locNameById[artwork.location_id]
          : t("round36.collectionBreakdown.unlocated");

      locationMap[locLabel] = (locationMap[locLabel] || 0) + value;

      const artist =
        (typeof artwork?.artist === "string" ? artwork.artist : "").trim() || t("round36.collectionBreakdown.unknownArtist");

      artistMap[artist] = (artistMap[artist] || 0) + 1;
    }

    const sortDesc = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]);
    const toPeriodNumber = (key) => (key === UNKNOWN_KEY ? Infinity : parseInt(key, 10));

    const mediumSorted = sortDesc(mediumMap);
    const typeSorted = sortDesc(typeMap);
    const valueByMediumSorted = sortDesc(valueMediumMap);
    const locationSorted = sortDesc(locationMap).slice(0, 20);
    // « Inconnu » sorti des barres (il écrase les décennies réelles) et exposé
    // en légende sous le graphe → distribution lisible + manque rendu visible.
    const unknownPeriodCount = decadeMap[UNKNOWN_KEY] || 0;
    const decades = Object.entries(decadeMap)
      .filter(([key]) => key !== UNKNOWN_KEY)
      .sort((a, b) => toPeriodNumber(a[0]) - toPeriodNumber(b[0]));

    setMediumData({
      keys: mediumSorted.map(([key]) => key),
      labels: mediumSorted.map(([key]) => translateMedium(t, key)),
      values: mediumSorted.map(([, value]) => value),
    });

    setTypeData({
      keys: typeSorted.map(([key]) => key),
      labels: typeSorted.map(([key]) => getTypeOfArtLabel(key, t)),
      values: typeSorted.map(([, value]) => value),
    });

    setValueByMedium({
      keys: valueByMediumSorted.map(([key]) => key),
      labels: valueByMediumSorted.map(([key]) => translateMedium(t, key)),
      values: valueByMediumSorted.map(([, value]) => value),
    });

    setLocationData({
      keys: locationSorted.map(([key]) => key),
      labels: locationSorted.map(([key]) => key),
      values: locationSorted.map(([, value]) => value),
    });

    setPeriodData({
      keys: decades.map(([key]) => key),
      labels: decades.map(([key]) => translatePeriod(t, key)),
      values: decades.map(([, value]) => value),
    });
    setPeriodUnknown(unknownPeriodCount);
    setPeriodUnknownLot(unknownInLot);

    const artistSorted = sortDesc(artistMap);
    const hasTop10 = artistSorted.some(([, value]) => value >= 10);
    const artistSlice = hasTop10 ? artistSorted.slice(0, 10) : artistSorted.slice(0, 5);

    setArtistData({
      keys: artistSlice.map(([key]) => key),
      labels: artistSlice.map(([key]) => key),
      values: artistSlice.map(([, value]) => value),
    });
  }, [artworks, locations, collections, locationBreakdownMode, t]);

  const mediumColors = useMemo(
    () => greyUnknowns(mediumData.keys, makePalette(mediumData.keys.length)),
    [mediumData]
  );

  const typeColors = useMemo(
    () => greyUnknowns(typeData.keys, makePalette(typeData.keys.length)),
    [typeData]
  );

  const valueMediumColors = useMemo(
    () =>
      greyUnknowns(
        valueByMedium.keys,
        makePalette(valueByMedium.keys.length, { s: 40, l: 58 })
      ),
    [valueByMedium]
  );

  const periodColors = useMemo(
    () => greyUnknowns(periodData.keys, makePalette(Math.max(periodData.keys.length, 1), { s: 38, l: 58 })),
    [periodData]
  );

  const locationColors = useMemo(
    () => makePalette(locationData.labels.length, { s: 35, l: 60 }),
    [locationData]
  );

  const artistColors = useMemo(
    () => makePalette(artistData.labels.length, { s: 40, l: 55 }),
    [artistData]
  );

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "right",
          align: "center",
          labels: {
            font: { size: 10, family: "system-ui, sans-serif" },
            padding: 8,
            boxWidth: 10,
            boxHeight: 10,
          },
        },
        tooltip: {
          backgroundColor: "rgba(26,26,26,0.9)",
          padding: 12,
          callbacks: {
            label: (ctx) => {
              const value = ctx.parsed;
              const total = ctx.dataset.data.reduce((sum, item) => sum + item, 0);
              const percent = total ? ((value / total) * 100).toFixed(1) : 0;
              return `${ctx.label}: ${value} (${percent}%)`;
            },
          },
        },
      },
    }),
    []
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(26,26,26,0.9)",
          padding: 12,
          callbacks: {
            label: (ctx) =>
              ctx.dataset.label === t("round36.collectionBreakdown.totalValue")
                ? new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language || undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(ctx.parsed.x))
                : t("round36.collectionBreakdown.artworkCount", { count: ctx.parsed.x }),
          },
        },
      },
      scales: {
        x: { grid: { color: "#f0ede8" }, ticks: { font: { size: 11 } } },
        y: {
          grid: { display: false },
          ticks: { font: { size: 11, family: "Georgia, serif" } },
        },
      },
    }),
    [t, i18n.language, i18n.resolvedLanguage]
  );

  const periodBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(26,26,26,0.9)",
          padding: 12,
          callbacks: {
            label: (ctx) => t("round36.collectionBreakdown.artworkCount", { count: ctx.parsed.y }),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 11, family: "Georgia, serif" } },
        },
        y: { grid: { color: "#f0ede8" }, ticks: { font: { size: 11 } } },
      },
    }),
    [t]
  );

  const mediumChartData = useMemo(
    () => ({
      labels: mediumData.labels,
      datasets: [{ data: mediumData.values, backgroundColor: mediumColors, borderWidth: 0 }],
    }),
    [mediumData, mediumColors]
  );

  const typeChartData = useMemo(
    () => ({
      labels: typeData.labels,
      datasets: [{ data: typeData.values, backgroundColor: typeColors, borderWidth: 0 }],
    }),
    [typeData, typeColors]
  );

  const valueByMediumChartData = useMemo(
    () => ({
      labels: valueByMedium.labels,
      datasets: [
        {
          label: t("round36.collectionBreakdown.totalValue"),
          data: valueByMedium.values,
          backgroundColor: valueMediumColors,
          borderRadius: 6,
        },
      ],
    }),
    [valueByMedium, valueMediumColors, t]
  );

  const periodChartData = useMemo(
    () => ({
      labels: periodData.labels,
      datasets: [
        {
          label: t("round36.collectionBreakdown.numberOfArtworks"),
          data: periodData.values,
          backgroundColor: periodColors,
          borderRadius: 6,
        },
      ],
    }),
    [periodData, periodColors, t]
  );

  const locationChartData = useMemo(
    () => ({
      labels: locationData.labels,
      datasets: [
        {
          label: t("round36.collectionBreakdown.totalValue"),
          data: locationData.values,
          backgroundColor: locationColors,
          borderRadius: 6,
        },
      ],
    }),
    [locationData, locationColors, t]
  );

  const artistChartData = useMemo(
    () => ({
      labels: artistData.labels,
      datasets: [
        {
          label: t("round36.collectionBreakdown.numberOfArtworks"),
          data: artistData.values,
          backgroundColor: artistColors,
          borderRadius: 6,
        },
      ],
    }),
    [artistData, artistColors, t]
  );

  const totalOeuvres = artworks.length;
  const totalValeur = artworks.reduce(
    (sum, artwork) => sum + (Number(artwork?.value ?? artwork?.estimated_value ?? 0) || 0),
    0
  );

  const cardStyle = {
    background: "#fff",
    border: "1px solid #ece7df",
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  };

  const chartTitleStyle = {
    fontSize: 16,
    fontFamily: "Georgia, serif",
    fontWeight: 400,
    margin: "0 0 16px 0",
    color: "#161616",
  };

  return (
    <section style={{ marginTop: 16, marginBottom: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <p
          style={{
            fontSize: 13,
            color: "#999",
            margin: 0,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          {loading
            ? t("round36.collectionBreakdown.loading")
            : loadError
            ? loadError
            : totalOeuvres > 0
            ? t("round36.collectionBreakdown.summary", { count: totalOeuvres, value: totalValeur > 0 ? new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language || undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(totalValeur) : "" })
            : t("round36.collectionBreakdown.noArtworkSelection")}
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label
            htmlFor="cb-select"
            style={{
              fontSize: 11,
              color: "#aaa",
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            {t("round36.collectionBreakdown.collectionLabel")}
          </label>

          <select
            id="cb-select"
            value={selectedCollectionId}
            onChange={(e) => setSelectedCollectionId(e.target.value)}
            style={{
              height: 36,
              borderRadius: 8,
              border: "1px solid #ddd6ce",
              background: "#fff",
              padding: "0 12px",
              fontSize: 13,
              color: "#222",
              cursor: "pointer",
              outline: "none",
            }}
          >
            <option value={ALL}>{t("round36.collectionBreakdown.allCollections")}</option>
            {collections.map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.title || t("round36.collectionBreakdown.untitled")}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ borderBottom: "1px solid #e5e3df", marginBottom: 24 }} />

      {!loading && !loadError && totalOeuvres === 0 && (
        <div
          style={{
            ...cardStyle,
            color: "#8c8c8c",
            fontSize: 14,
            fontFamily: "system-ui, sans-serif",
          }}
        >
          {t("round36.collectionBreakdown.noArtworkAvailable")}
        </div>
      )}

      {totalOeuvres > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 20,
            overflowX: "hidden",
          }}
        >
          <div style={cardStyle}>
            <h3 style={chartTitleStyle}>{t("round36.collectionBreakdown.charts.medium")}</h3>
            <div style={{ position: "relative", height: 260 }}>
              <Doughnut data={mediumChartData} options={doughnutOptions} />
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={chartTitleStyle}>{t("round36.collectionBreakdown.charts.type")}</h3>
            <div style={{ position: "relative", height: 260 }}>
              <Doughnut data={typeChartData} options={doughnutOptions} />
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={chartTitleStyle}>{t("round36.collectionBreakdown.charts.period")}</h3>
            <div style={{ position: "relative", height: 320 }}>
              <Bar data={periodChartData} options={periodBarOptions} />
            </div>
            {periodUnknown > 0 && (
              <p style={{ marginTop: 10, fontSize: 12, color: "#8a8378", lineHeight: 1.4 }}>
                {t("round36.collectionBreakdown.periodUnknownNote", {
                  count: periodUnknown,
                  defaultValue: "{{count}} œuvre(s) sans année de création renseignée (non affichées ci-dessus).",
                })}
                {periodUnknownLot > 0 && (
                  <>
                    {" "}
                    <span style={{ color: "#6b6459" }}>
                      {t("round36.collectionBreakdown.periodUnknownLotNote", {
                        count: periodUnknownLot,
                        defaultValue:
                          "Dont {{count}} en lot : renseignez l’année du lot pour les dater.",
                      })}
                    </span>
                  </>
                )}
              </p>
            )}
          </div>

          {valueByMedium.values.some((value) => value > 0) && (
            <div style={cardStyle}>
              <h3 style={chartTitleStyle}>{t("round36.collectionBreakdown.charts.valueByMedium")}</h3>
              <div style={{ position: "relative", height: 320 }}>
                <Bar data={valueByMediumChartData} options={barOptions} />
              </div>
            </div>
          )}

          {locationData.labels.length > 0 && locationData.values.some((value) => value > 0) && (
            <div style={{ ...cardStyle, gridColumn: "1 / -1" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <h3 style={{ ...chartTitleStyle, margin: 0 }}>
                  {locationBreakdownMode === "collection"
                    ? t("round36.collectionBreakdown.charts.valueByCollection")
                    : t("round36.collectionBreakdown.charts.valueByLocation")}
                </h3>

                <div
                  role="group"
                  aria-label={t("round36.collectionBreakdown.modeAria")}
                  style={{
                    display: "inline-flex",
                    padding: 3,
                    border: "1px solid #e6ded3",
                    borderRadius: 999,
                    background: "#faf8f4",
                    gap: 2,
                  }}
                >
                  {[
                    ["location", t("round36.collectionBreakdown.modes.locations")],
                    ["collection", t("round36.collectionBreakdown.modes.collections")],
                  ].map(([mode, label]) => {
                    const active = locationBreakdownMode === mode;

                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setLocationBreakdownMode(mode)}
                        style={{
                          border: "none",
                          borderRadius: 999,
                          background: active ? "#161616" : "transparent",
                          color: active ? "#fff" : "#6b6258",
                          padding: "7px 12px",
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: "system-ui, sans-serif",
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div
                style={{
                  position: "relative",
                  height: Math.max(220, locationData.labels.length * 40),
                }}
              >
                <Bar data={locationChartData} options={barOptions} />
              </div>
            </div>
          )}

          {artistData.labels.length > 0 && (
            <div style={{ ...cardStyle, gridColumn: "1 / -1" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <h3 style={{ ...chartTitleStyle, margin: 0 }}>
                  {artistData.values.some((value) => value >= 10)
                    ? t("round36.collectionBreakdown.charts.top10Artists")
                    : t("round36.collectionBreakdown.charts.top5Artists")}
                </h3>

                <span
                  style={{
                    fontSize: 11,
                    color: "#aaa",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    fontFamily: "system-ui, sans-serif",
                  }}
                >
                  {t("round36.collectionBreakdown.byArtworkCount")}
                </span>
              </div>

              <div
                style={{
                  position: "relative",
                  height: Math.max(220, artistData.labels.length * 44),
                }}
              >
                <Bar
                  data={artistChartData}
                  options={{
                    ...barOptions,
                    plugins: {
                      ...barOptions.plugins,
                      tooltip: {
                        backgroundColor: "rgba(26,26,26,0.9)",
                        padding: 12,
                        callbacks: {
                          label: (ctx) =>
                            t("round36.collectionBreakdown.artworkCount", { count: ctx.parsed.x }),
                        },
                      },
                    },
                    scales: {
                      ...barOptions.scales,
                      y: {
                        ...barOptions.scales.y,
                        ticks: {
                          font: { size: 12, family: "Georgia, serif" },
                          color: "#1a1a1a",
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}